import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Sparkles, Trash2, Loader2, RefreshCw, Play } from 'lucide-react';
import { API } from './shared';

const ACTIVE = ['queued', 'running'];

export function AiTrailerButton({ contentId, hasTrailer, aiGenerated, trailerUrl, light, onDone, onDeleted }) {
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);
  const doneRef = useRef(false);

  const poll = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/trailers/status/${contentId}`, { withCredentials: true });
      setJob(data);
      return data;
    } catch { return null; }
  }, [contentId]);

  useEffect(() => { poll(); }, [poll]);

  const isActive = job && ACTIVE.includes(job.status);

  useEffect(() => {
    if (!isActive) return;
    const iv = setInterval(async () => {
      const d = await poll();
      if (d && d.status === 'done' && d.trailer_url && !doneRef.current) {
        doneRef.current = true;
        onDone?.(d.trailer_url);
        setPreview(true);
      }
    }, 4000);
    return () => clearInterval(iv);
  }, [isActive, poll, onDone]);

  const start = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/trailers/generate`, { content_id: contentId }, { withCredentials: true });
      doneRef.current = false;
      await poll();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur lors du lancement'); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/trailers/${contentId}`, { withCredentials: true });
      setJob(null);
      setPreview(false);
      onDeleted?.();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  const btnCls = light
    ? 'border-gray-200 text-gray-600 hover:border-violet-500 hover:text-violet-600'
    : 'border-white/10 text-white/60 hover:border-violet-400 hover:text-violet-300';

  if (isActive) {
    return (
      <div data-testid="ai-trailer-progress" className={`mt-2 p-3 rounded-lg border ${light ? 'bg-violet-50 border-violet-200' : 'bg-violet-500/5 border-violet-500/20'}`}>
        <div className="flex items-center gap-2 mb-1.5">
          <Loader2 size={12} className="animate-spin text-violet-400" />
          <p className={`text-[10px] font-semibold ${light ? 'text-violet-700' : 'text-violet-300'}`}>
            Bande-annonce IA · {job.step || 'En cours…'}
            {job.status === 'queued' && job.queue_position > 1 ? ` (position ${job.queue_position})` : ''}
          </p>
        </div>
        <div className={`h-1.5 rounded-full overflow-hidden ${light ? 'bg-violet-100' : 'bg-white/5'}`}>
          <div className="h-full bg-violet-500 rounded-full transition-all duration-700" style={{ width: `${job.progress || 2}%` }} />
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="mt-2 flex flex-wrap items-center gap-2">
      {!hasTrailer && (
        <button type="button" data-testid="ai-trailer-generate-btn" onClick={start} disabled={busy}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold border transition-colors disabled:opacity-50 ${btnCls}`}>
          {busy ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />} Générer la bande-annonce (IA)
        </button>
      )}
      {hasTrailer && !aiGenerated && (
        <button type="button" data-testid="ai-trailer-replace-btn" onClick={start} disabled={busy}
          title="Remplace la bande-annonce actuelle par une bande-annonce générée automatiquement"
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold border transition-colors disabled:opacity-50 ${btnCls}`}>
          {busy ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />} Générer avec l'IA (remplace la BA actuelle)
        </button>
      )}
      {hasTrailer && aiGenerated && (
        <>
          <span className="flex items-center gap-1 px-2 py-0.5 rounded bg-violet-500/15 text-violet-400 text-[9px] font-bold" data-testid="ai-trailer-badge">
            <Sparkles size={9} /> BA générée par IA
          </span>
          <button type="button" data-testid="ai-trailer-preview-btn" onClick={() => setPreview(v => !v)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold border transition-colors ${preview ? 'border-violet-400 text-violet-400' : btnCls}`}>
            <Play size={11} /> {preview ? "Fermer l'aperçu" : 'Aperçu'}
          </button>
          <button type="button" data-testid="ai-trailer-regenerate-btn" onClick={start} disabled={busy}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold border transition-colors disabled:opacity-50 ${btnCls}`}>
            <RefreshCw size={11} /> Régénérer (IA)
          </button>
          <button type="button" data-testid="ai-trailer-delete-btn" onClick={remove} disabled={busy}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold border transition-colors disabled:opacity-50 ${light ? 'border-gray-200 text-gray-500 hover:border-red-400 hover:text-red-500' : 'border-white/10 text-white/50 hover:border-red-400 hover:text-red-400'}`}>
            <Trash2 size={11} /> Supprimer la BA IA
          </button>
        </>
      )}
      {job?.status === 'error' && (
        <span className="text-[9px] text-red-400 font-semibold" data-testid="ai-trailer-error">Échec : {job.error}</span>
      )}
    </div>
    {preview && trailerUrl && (
      <div className="mt-2" data-testid="ai-trailer-preview">
        <video src={trailerUrl} controls autoPlay className={`w-full max-w-xl rounded-lg border ${light ? 'border-gray-200' : 'border-white/10'}`} data-testid="ai-trailer-preview-video" />
        {job?.segment?.reason && <p className={`text-[9px] mt-1 italic max-w-xl ${light ? 'text-gray-400' : 'text-white/35'}`}>Choix de l'IA : {job.segment.reason}</p>}
      </div>
    )}
    </>
  );
}

export function AiTrailerBulkButton({ light }) {
  const [activeCount, setActiveCount] = useState(0);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/trailers/jobs`, { withCredentials: true });
      setActiveCount((data.jobs || []).length);
    } catch {}
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!activeCount) return;
    const iv = setInterval(refresh, 6000);
    return () => clearInterval(iv);
  }, [activeCount, refresh]);

  const launch = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/trailers/generate-all`, {}, { withCredentials: true });
      alert(`Génération IA lancée : ${data.queued} bande(s)-annonce(s) en file d'attente.` +
        (data.skipped_no_video ? `\n${data.skipped_no_video} contenu(s) ignoré(s) (aucune vidéo source).` : '') +
        (data.skipped_in_progress ? `\n${data.skipped_in_progress} déjà en cours.` : ''));
      refresh();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  return (
    <button onClick={launch} disabled={busy} data-testid="ai-trailer-bulk-btn"
      title="Génère automatiquement par IA une bande-annonce pour tous les contenus qui n'en ont pas"
      className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border transition-colors disabled:opacity-50 ${
        activeCount
          ? 'border-violet-500/40 bg-violet-500/10 text-violet-400'
          : light ? 'border-gray-200 bg-white text-gray-600 hover:border-violet-400 hover:text-violet-600' : 'border-white/10 bg-white/5 text-white/60 hover:border-violet-400 hover:text-violet-300'
      }`}>
      {activeCount ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
      {activeCount ? `BA IA : ${activeCount} en cours` : 'BA manquantes (IA)'}
    </button>
  );
}
